import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  RefreshControl,
  TouchableOpacity,
} from 'react-native';
import {
  Text,
  Searchbar,
  FAB,
  ActivityIndicator,
  Snackbar,
} from 'react-native-paper';
import GradientHeader from '../../components/common/GradientHeader';
import {useTranslation} from 'react-i18next';
import {useNavigation, DrawerActions} from '@react-navigation/native';
import {useCustomerStore} from '../../store/useCustomerStore';
import {colors, spacing} from '../../theme';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

const CustomerListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {
    customers,
    isLoading,
    pagination,
    fetchCustomers,
    searchCustomers,
  } = useCustomerStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    fetchCustomers({page: 0, size: 20});
  }, [fetchCustomers]);

  const handleSearch = useCallback(
    query => {
      setSearchQuery(query);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        try {
          if (query.trim().length > 0) {
            await searchCustomers(query.trim());
          } else {
            await fetchCustomers({page: 0, size: 20});
          }
        } catch (e) {
          setSnackbar(e.response?.data?.message || e.message || t('common.error'));
        }
      }, 300);
    },
    [searchCustomers, fetchCustomers, t],
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (searchQuery.trim().length > 0) {
        await searchCustomers(searchQuery.trim());
      } else {
        await fetchCustomers({page: 0, size: 20});
      }
    } finally {
      setRefreshing(false);
    }
  }, [fetchCustomers, searchCustomers, searchQuery]);

  const handleLoadMore = useCallback(() => {
    if (isLoading || searchQuery.trim().length > 0) {
      return;
    }
    const nextPage = pagination.page + 1;
    if (nextPage < pagination.totalPages) {
      fetchCustomers({page: nextPage, size: pagination.size});
    }
  }, [isLoading, pagination, fetchCustomers, searchQuery]);

  const handleCustomerPress = useCallback(
    customerId => {
      navigation.navigate('CustomerDetail', {customerId});
    },
    [navigation],
  );

  const handleAddCustomer = useCallback(() => {
    navigation.navigate('CustomerForm');
  }, [navigation]);

  const getGenderIcon = (gender) => {
    if (gender === 'MALE') return 'gender-male';
    if (gender === 'FEMALE') return 'gender-female';
    return null;
  };

  const renderCustomerCard = useCallback(
    ({item}) => {
      const genderIcon = getGenderIcon(item.gender);
      const locationParts = [item.city, item.pincode].filter(Boolean);
      const locationText = locationParts.join(' - ');

      return (
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={() => handleCustomerPress(item.id)}>
          <View style={styles.card}>
            <View style={[styles.accentBar, {backgroundColor: item.gender === 'FEMALE' ? '#E91E63' : colors.primary}]} />
            <View style={styles.cardBody}>
              <View style={styles.avatar}>
                <Text style={styles.avatarText}>
                  {(item.fullName || '?').charAt(0).toUpperCase()}
                </Text>
              </View>
              <View style={styles.cardInfo}>
                <View style={styles.nameRow}>
                  <Text style={styles.customerName} numberOfLines={1}>{item.fullName}</Text>
                  {genderIcon && (
                    <Icon name={genderIcon} size={14} color={item.gender === 'FEMALE' ? '#E91E63' : colors.info} />
                  )}
                </View>
                {item.phone ? (
                  <Text style={styles.customerPhone}>{item.phone}</Text>
                ) : null}
                {locationText ? (
                  <View style={styles.locationRow}>
                    <Icon name="map-marker-outline" size={12} color={colors.textLight} />
                    <Text style={styles.locationText} numberOfLines={1}>{locationText}</Text>
                  </View>
                ) : null}
              </View>
              <Icon name="chevron-right" size={20} color={colors.textLight} />
            </View>
          </View>
        </TouchableOpacity>
      );
    },
    [handleCustomerPress],
  );

  const renderEmpty = useCallback(() => {
    if (isLoading) {
      return null;
    }
    return (
      <View style={styles.emptyContainer}>
        <Icon name="account-group-outline" size={56} color={colors.textLight} />
        <Text style={styles.emptyTitle}>
          {searchQuery.trim().length > 0
            ? t('common.noResults') || 'No results found'
            : t('common.noData') || 'No customers yet'}
        </Text>
        <Text style={styles.emptySubtitle}>
          {searchQuery.trim().length > 0
            ? (t('common.tryDifferentSearch') || 'Try a different search term')
            : (t('common.tapToAdd') || 'Tap + to add your first customer')}
        </Text>
      </View>
    );
  }, [isLoading, searchQuery, t]);

  const renderFooter = useCallback(() => {
    if (!isLoading || customers.length === 0) {
      return null;
    }
    return (
      <View style={styles.footer}>
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    );
  }, [isLoading, customers.length]);

  const keyExtractor = useCallback(item => String(item.id), []);

  return (
    <View style={styles.container}>
      <GradientHeader
        title={t('customer.title')}
        onMenu={() => navigation.dispatch(DrawerActions.openDrawer())}
      />

      <View style={styles.searchContainer}>
        <Searchbar
          placeholder={t('customer.searchPlaceholder')}
          value={searchQuery}
          onChangeText={handleSearch}
          style={styles.searchbar}
          inputStyle={styles.searchInput}
          iconColor={colors.textSecondary}
        />
      </View>

      {isLoading && customers.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={customers}
          renderItem={renderCustomerCard}
          keyExtractor={keyExtractor}
          contentContainerStyle={styles.listContent}
          ListEmptyComponent={renderEmpty}
          ListFooterComponent={renderFooter}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.3}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              colors={[colors.primary]}
              tintColor={colors.primary}
            />
          }
        />
      )}

      <FAB
        icon="plus"
        style={styles.fab}
        onPress={handleAddCustomer}
        color={colors.white}
      />

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  searchContainer: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.surface,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
  },
  searchbar: {
    backgroundColor: colors.background,
    elevation: 0,
  },
  searchInput: {
    fontSize: 14,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: 80,
  },

  // Card
  card: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderRadius: 10,
    marginBottom: spacing.sm,
    overflow: 'hidden',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  accentBar: {
    width: 4,
  },
  cardBody: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
    paddingRight: spacing.md,
    gap: spacing.sm,
  },
  avatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: colors.primary + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    color: colors.primary,
    fontSize: 18,
    fontWeight: '700',
  },
  cardInfo: {
    flex: 1,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  customerName: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.text,
    flexShrink: 1,
  },
  customerPhone: {
    fontSize: 13,
    color: colors.textSecondary,
    marginTop: 1,
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    marginTop: 2,
  },
  locationText: {
    fontSize: 12,
    color: colors.textLight,
  },

  // Empty
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: spacing.xxl * 2,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginTop: spacing.md,
    marginBottom: spacing.xs,
  },
  emptySubtitle: {
    fontSize: 14,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  footer: {
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  fab: {
    position: 'absolute',
    right: spacing.md,
    bottom: spacing.md,
    backgroundColor: colors.primary,
  },
});

export default CustomerListScreen;
